import type { dynamicObj } from 'src/pages/assets';
import { setWithExpiry, getWithExpiry } from 'src/utils/cacheHelper';
import { logger } from 'src/utils/logger';

export const UserOrg = {
  async set() {
    try {
      const { userApiService } = await import('src/services');
      const response = await userApiService.getUserOrganizations();
      logger.debug('[UserOrg] API Response received:', response);
      logger.debug('[UserOrg] Response type:', typeof response);
      logger.debug('[UserOrg] Is array?', Array.isArray(response));

      // Handle both API response formats:
      // - Old format: response is the array directly [{ id, name, sub_organisations }]
      // - New format: response is an object { organisations: [...], subscription: [...], user: {...} }
      let organizations: dynamicObj[];

      if (Array.isArray(response)) {
        // Check if this is a flat sub-org array (Format 2: Dev)
        // Flat sub-orgs have 'org_id' property but no 'sub_organisations' property
        if (
          response.length > 0 &&
          response[0].org_id &&
          !response[0].sub_organisations
        ) {
          // Transform flat sub-org array to expected nested format
          const orgId = response[0].org_id;
          organizations = [
            {
              id: orgId,
              name: 'Organization', // Placeholder - main org name not available in this response
              sub_organisations: response,
            },
          ];
          logger.debug(
            '[UserOrg] Transformed flat sub-org array to nested format',
          );
        } else {
          // Format 3: Standard array with parent org containing sub_organisations
          logger.debug('[UserOrg] Using standard format: response is array directly');
          organizations = response;
        }
      } else if (
        response &&
        response.organisations &&
        Array.isArray(response.organisations)
      ) {
        // New format: extract organisations array from wrapper object
        logger.debug(
          '[UserOrg] Using new format: extracting response.organisations',
        );
        organizations = response.organisations;
      } else {
        logger.warn('[UserOrg] ⚠️ API response is empty or has unexpected format!');
        organizations = [];
      }

      if (organizations.length > 0) {
        logger.debug('[UserOrg] First organization:', organizations[0]);
        logger.debug(
          '[UserOrg] sub_organisations field exists?',
          'sub_organisations' in organizations[0],
        );
        logger.debug(
          '[UserOrg] sub_organisations value:',
          organizations[0].sub_organisations,
        );
        logger.debug(
          '[UserOrg] sub_organisations type:',
          typeof organizations[0].sub_organisations,
        );
        logger.debug(
          '[UserOrg] sub_organisations is array?',
          Array.isArray(organizations[0].sub_organisations),
        );
        logger.debug(
          '[UserOrg] sub_organisations length:',
          organizations[0].sub_organisations?.length,
        );
      }

      setWithExpiry('organizations', organizations);
      logger.debug('[UserOrg] ✓ Organizations stored in cache');
    } catch (err) {
      logger.error('[UserOrg] ❌ Failed to fetch organizations:', err);
      setWithExpiry('organizations', []);
    }
  },

  getFromCache() {
    const organizations = getWithExpiry('organizations') as dynamicObj[];
    logger.debug('[UserOrg] Retrieved from cache:', organizations);
    logger.debug('[UserOrg] Cache is array?', Array.isArray(organizations));

    if (
      organizations &&
      Array.isArray(organizations) &&
      organizations.length > 0
    ) {
      logger.debug('[UserOrg] Cached first organization:', organizations[0]);
      logger.debug(
        '[UserOrg] Cached sub_organisations:',
        organizations[0].sub_organisations,
      );
      logger.debug(
        '[UserOrg] Cached sub_organisations length:',
        organizations[0].sub_organisations?.length,
      );
    } else {
      // Changed from warn to debug - this is expected during initial page load
      // Organizations are fetched asynchronously, so cache starts empty
      logger.debug('[UserOrg] Cache is empty or invalid - will fetch from API');
    }

    return organizations;
  },

  get() {
    try {
      return this.getFromCache();
    } catch (err) {
      logError(err);
      return [];
    }
  },

  async fetchFromServer() {
    try {
      await this.set();
      return this.get();
    } catch (err) {
      logError(err);
    }
  },
};
